export type CatalogueShareCountry = {
  iso: string;
  dial: string;
  label: string;
};

/** Common HKP buyer markets. Default is India / +91. */
export const CATALOGUE_SHARE_COUNTRIES: CatalogueShareCountry[] = [
  { iso: "IN", dial: "91", label: "India (+91)" },
  { iso: "AE", dial: "971", label: "United Arab Emirates (+971)" },
  { iso: "GB", dial: "44", label: "United Kingdom (+44)" },
  { iso: "US", dial: "1", label: "United States (+1)" },
  { iso: "CA", dial: "1", label: "Canada (+1)" },
  { iso: "SA", dial: "966", label: "Saudi Arabia (+966)" },
  { iso: "QA", dial: "974", label: "Qatar (+974)" },
  { iso: "KW", dial: "965", label: "Kuwait (+965)" },
  { iso: "BH", dial: "973", label: "Bahrain (+973)" },
  { iso: "OM", dial: "968", label: "Oman (+968)" },
  { iso: "SG", dial: "65", label: "Singapore (+65)" },
  { iso: "MY", dial: "60", label: "Malaysia (+60)" },
  { iso: "AU", dial: "61", label: "Australia (+61)" },
  { iso: "DE", dial: "49", label: "Germany (+49)" },
  { iso: "FR", dial: "33", label: "France (+33)" },
  { iso: "IT", dial: "39", label: "Italy (+39)" },
  { iso: "ES", dial: "34", label: "Spain (+34)" },
  { iso: "NL", dial: "31", label: "Netherlands (+31)" },
  { iso: "CH", dial: "41", label: "Switzerland (+41)" },
  { iso: "JP", dial: "81", label: "Japan (+81)" },
  { iso: "CN", dial: "86", label: "China (+86)" },
  { iso: "PK", dial: "92", label: "Pakistan (+92)" },
  { iso: "BD", dial: "880", label: "Bangladesh (+880)" },
  { iso: "LK", dial: "94", label: "Sri Lanka (+94)" },
  { iso: "NP", dial: "977", label: "Nepal (+977)" },
];

export const DEFAULT_SHARE_COUNTRY_ISO = "IN";

const EMAIL_PATTERN = /^[A-Za-z0-9+_.'-]+@[A-Za-z0-9][A-Za-z0-9.-]*\.[A-Za-z]{2,}$/;

export function findShareCountry(isoOrDial: string | undefined): CatalogueShareCountry | undefined {
  const raw = String(isoOrDial || "").trim();
  if (!raw) return undefined;
  const upper = raw.replace(/^\+/, "").toUpperCase();
  return (
    CATALOGUE_SHARE_COUNTRIES.find((c) => c.iso === upper) ||
    CATALOGUE_SHARE_COUNTRIES.find((c) => c.dial === upper.replace(/\D/g, ""))
  );
}

export function isIndiaShareCountry(isoOrDial: string | undefined): boolean {
  const country = findShareCountry(isoOrDial);
  if (country) return country.iso === "IN";
  const digits = String(isoOrDial || "").replace(/\D/g, "");
  return digits === "91" || String(isoOrDial || "").toUpperCase() === "IN";
}

export function isValidShareEmail(raw: string | undefined): boolean {
  const email = String(raw || "").trim().toLowerCase();
  return email.length > 0 && EMAIL_PATTERN.test(email);
}

/** Local national digits as typed (leading 0 allowed). Empty if nothing usable. */
export function nationalPhoneDigits(raw: string): string {
  return String(raw || "").replace(/\D/g, "");
}

export function isPlausibleSharePhone(raw: string, countryIso: string): boolean {
  const digits = nationalPhoneDigits(raw);
  if (isIndiaShareCountry(countryIso)) {
    const national =
      digits.length === 12 && digits.startsWith("91")
        ? digits.slice(2)
        : digits.replace(/^0+/, "");
    return /^[6-9]\d{9}$/.test(national);
  }
  const national = digits.replace(/^0+/, "");
  return national.length >= 6 && national.length <= 15;
}

/** ISO for an invite country code (dial or ISO). Undefined when unknown so the gate can stay selectable. */
export function inviteCountryIso(recipientCountryCode: string | undefined): string | undefined {
  return findShareCountry(recipientCountryCode)?.iso;
}

export function formatSharePhoneDisplay(
  phone: string | undefined,
  countryCode: string | undefined
): string {
  const raw = String(phone || "").trim();
  if (!raw) return "—";
  const digits = raw.replace(/\D/g, "");
  if (!digits) return raw;
  const country = findShareCountry(countryCode);
  const dial = country?.dial || String(countryCode || "").replace(/\D/g, "");
  if (digits.length === 10 && (!dial || dial === "91")) {
    return `+91 ${digits}`;
  }
  if (dial && digits.startsWith(dial) && digits.length > dial.length) {
    return `+${dial} ${digits.slice(dial.length)}`;
  }
  if (dial && !digits.startsWith(dial)) {
    return `+${dial} ${digits}`;
  }
  return `+${digits}`;
}

/** Stored party phone → country + the local digits the share form expects. */
export function partyPhoneToShareFields(raw: string | undefined): { countryIso: string; phone: string } {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) {
    return { countryIso: DEFAULT_SHARE_COUNTRY_ISO, phone: "" };
  }
  if (digits.length === 10 && /^[6-9]/.test(digits)) {
    return { countryIso: "IN", phone: digits };
  }
  if (digits.length === 12 && digits.startsWith("91") && /^[6-9]/.test(digits.slice(2))) {
    return { countryIso: "IN", phone: digits.slice(2) };
  }
  const byDial = [...CATALOGUE_SHARE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const country of byDial) {
    if (country.iso === "IN") continue;
    if (!digits.startsWith(country.dial)) continue;
    const national = digits.slice(country.dial.length).replace(/^0+/, "");
    if (national.length >= 6 && national.length <= 15) {
      return { countryIso: country.iso, phone: national };
    }
  }
  return { countryIso: DEFAULT_SHARE_COUNTRY_ISO, phone: digits };
}

export function whatsappDigits(countryIso: string, localPhone: string): string {
  const country = findShareCountry(countryIso);
  const dial = country?.dial || "91";
  let digits = nationalPhoneDigits(localPhone);
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith(dial)) return digits;
  if (digits.startsWith("0")) digits = digits.replace(/^0+/, "");
  return `${dial}${digits}`;
}
